<template>
  <v-container class="insurance-container">
    <div v-if="loading" class="text-center">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else>
      <router-view
        :testator="testator"
        :quotes="quotes"
        :startLeadBody="startLeadBody"
        :button-loading="buttonLoading"
        :people-relations="peopleRelations"
        @updateInsurance="updateInsurance"
        @updateInsuranceAndRequestQuotes="updateInsuranceAndRequestQuotes"
        @startLead="startLead"
        @displayError="displayErrorSnackbar"
      />
    </div>

    <div v-if="showOmartImage" class="omart-container">
      <div class="omart-image-container mobile-omart-image">
        <v-img :src="mobileFooterSrc" alt="Old Mutual" />
      </div>
      <div class="omart-image-container desktop-omart-image">
        <v-img :src="desktopFooterSrc" alt="Old Mutual" />
      </div>
    </div>
    <div class="support-snippet-container">
      <SupportSnippet
        v-if="
          this.whitelabel.brand !== 'quickwill' && this.testatorHasNoAdvisor
        "
      />
    </div>
    <ErrorSnackbar v-model="showError" />
    <InformativeDialog
      :informativeDialog="informativeDialog"
      @informativeDialog="hideInformativeDialog"
    />
  </v-container>
</template>

<script>
  import {
    UPDATE_INSURANCE_MUTATION,
    GET_INSURANCE_QUOTES_MUTATION,
    START_LEAD_MUTATION,
  } from '../../constants/graphql/insurance';
  import { UPDATE_TESTATOR_PEOPLE } from '../../constants/graphql/testators';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import SupportSnippet from '../common/SupportSnippet.vue';
  import { getRelations } from '../../constants/js/people';
  import QuotesMap from '../../helpers/insurance/quotes_map';
  import InformativeDialog from '../common/dialogs/InformativeDialog.vue';

  export default {
    name: 'InsuranceBase',
    components: {
      ErrorSnackbar,
      SupportSnippet,
      InformativeDialog,
    },
    mixins: [formHelperMixin],
    beforeRouteUpdate(to, from, next) {
      if (
        to.meta.hideIfPolicyExists &&
        this.testator.insurance &&
        this.testator.insurance.rootPolicyNumber
      ) {
        next('/insurance/policy-confirmation');
      } else {
        next();
      }
    },
    data() {
      return {
        testator: {},
        loading: true,
        peopleRelations: {},
        quotes: [],
        startLeadBody: {},
        informativeDialog: null,
        startLeadResponse: null,
      };
    },
    computed: {
      mobileOmart() {
        return this.$vuetify.breakpoint.smAndDown;
      },
      showOmartImage() {
        return (
          this.$route.meta.showOmartImg && this.whitelabel.brand !== 'quickwill'
        );
      },
      isOmpCompany() {
        return !!this.testator?.enabledFeatures?.includes(
          'omp_financial_data_sq'
        );
      },
      mobileFooterSrc() {
        return this.isOmpCompany
          ? require('@/assets/quickwill/insurance/FROM-OLDMUTUAL-mobile.svg')
          : require('@/assets/quickwill/insurance/OMART-footer-mobile.svg');
      },
      desktopFooterSrc() {
        return this.isOmpCompany
          ? require('@/assets/quickwill/insurance/FROM-OLDMUTUAL-desktop.svg')
          : require('@/assets/quickwill/insurance/OMART-footer-desktop.svg');
      },
      testatorHasNoAdvisor() {
        return !this.testator.advisorId;
      },
    },
    methods: {
      fetchTestator() {
        this.$apollo
          .query({
            query: TESTATOR_QUERY,
            variables: {
              id: this.testator.id,
            },
            fetchPolicy: 'network-only',
          })
          .then(response => {
            if (response.data && response.data.testator) {
              this.peopleRelations = getRelations(
                response.data.testator.people
              );
            }
          })
          .catch(error => {
            console.error('Error fetching testator:', error);
          });
      },
      updateInsuranceAndRequestQuotes(
        {
          insuranceInputDetails,
          redirectLocation,
          deletedNominatedBeneficiary,
          peopleInputDetails,
          chosenBenefitPackage,
        },
        skipLoading
      ) {
        if (skipLoading !== true) {
          this.buttonLoading = true;
        }

        this.updateChildren(peopleInputDetails, skipLoading).then(() => {
          this.updateInsurance(
            {
              insuranceInputDetails,
              redirectLocation: null,
              deletedNominatedBeneficiary,
              chosenBenefitPackage,
            },
            skipLoading
          ).then(() => {
            this.getInsuranceQuotes(redirectLocation, skipLoading);
          });
        });
      },
      startLead() {
        this.buttonLoading = true;

        const insuranceId = this.testator.insurance.id;
        this.$apollo
          .mutate({
            mutation: START_LEAD_MUTATION,
            variables: {
              insuranceId,
            },
          })
          .then(_response => {
            // this.startLeadResponse = _response;
            this.buttonLoading = false;
            // this.informativeDialog = true;
            // setTimeout(() => {
            //   this.informativeDialog = false;
            //   this.redirectToInsurance(this.startLeadResponse);
            // }, 4000);
            this.redirectToInsurance(_response);
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      redirectToInsurance(_response) {
        if (_response.data.startLead.leadStart.redirectUrl === 'internal') {
          this.$router.push({
            name: 'OverviewPage',
            params: {
              insuranceAlertMessage:
                'Thank you for submitting the interest in the Estate Assist products. An agent will contact the customer shortly.',
            },
          });
        } else if (_response.data.startLead.leadStart.newTab) {
          const userAgent = navigator.userAgent.toLowerCase();
          if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
            window.location.href =
              _response.data.startLead.leadStart.redirectUrl;
          } else {
            window.open(
              _response.data.startLead.leadStart.redirectUrl,
              '_blank'
            );
          }
        } else {
          window.location = _response.data.startLead.leadStart.redirectUrl;
        }
      },
      getInsuranceQuotes(redirectLocation, skipLoading) {
        if (skipLoading !== true) {
          this.buttonLoading = true;
        }

        const insuranceId = this.testator.insurance.id;
        this.$apollo
          .mutate({
            mutation: GET_INSURANCE_QUOTES_MUTATION,
            variables: {
              insuranceId,
            },
          })
          .then(_response => {
            this.buttonLoading = false;

            this.fetchTestator();
            this.quotes = QuotesMap.mapQuotes(
              _response.data.requestQuotes.quotes,
              this.whitelabel.images
            );

            this.startLeadBody = _response.data.requestQuotes.startLeadBody;

            if (redirectLocation) {
              this.$router.push({ name: redirectLocation });
            }
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateChildren(people, skipLoading) {
        if (skipLoading !== true) {
          this.buttonLoading = true;
        }

        if (!people || !people.people.length) {
          return Promise.resolve();
        }

        return this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_PEOPLE,
            variables: {
              id: this.testator.id,
              people: people,
            },
          })
          .then(_response => {
            return;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateInsurance(
        {
          insuranceInputDetails,
          redirectLocation,
          deletedNominatedBeneficiary,
        },
        skipLoading
      ) {
        if (skipLoading !== true) {
          this.buttonLoading = true;
        }
        return this.$apollo
          .mutate({
            mutation: UPDATE_INSURANCE_MUTATION,
            variables: {
              insuranceInputDetails,
            },
            update: (cache, { data: { updateInsurance } }) => {
              this.updateCacheAfterUpdateInsurance(
                cache,
                updateInsurance,
                deletedNominatedBeneficiary
              );

              if (insuranceInputDetails.chosenBenefitPackage) {
                const query = {
                  query: TESTATOR_QUERY,
                  variables: { id: this.testatorId },
                };
                const data = cache.readQuery(query);

                data.testator.estateCostCalculation.recommendedEstateCoverAmount =
                  insuranceInputDetails.chosenBenefitPackage;

                cache.writeQuery({ ...query, data: data });
              }
            },
          })
          .then(_response => {
            if (redirectLocation) {
              this.$router.push({ name: redirectLocation });
            }
            this.buttonLoading = false;
            return;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCacheAfterUpdateInsurance(
        cache,
        updateInsurance,
        deletedNominatedBeneficiary
      ) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.insurance = updateInsurance;

        // Delete nominated beneficiary
        if (deletedNominatedBeneficiary) {
          var person = data.testator.people.find(
            p => p.id == deletedNominatedBeneficiary
          );
          var testatorRelationshipIndex =
            person.testatorRelationships.findIndex(
              tr => tr.relation == 'INSURANCE_NOMINATED_BENEFICIARY'
            );
          person.testatorRelationships.splice(testatorRelationshipIndex, 1);

          // Delete person if no relationships left
          if (!person.testatorRelationships.length) {
            var personIndex = data.testator.people.findIndex(
              p => p.id == person.id
            );
            data.testator.people.splice(personIndex, 1);
          }
        }

        cache.writeQuery({ ...query, data: data });
      },
      displayErrorSnackbar() {
        this.displayError();
      },
      hideInformativeDialog() {
        this.informativeDialog = false;
        this.redirectToInsurance(this.startLeadResponse);
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (response.data && response.data.testator) {
            const enabledFeatures = response.data.testator.enabledFeatures;
            if (
              enabledFeatures.includes('liquidity_calculator') ||
              enabledFeatures.includes('full_insurance') ||
              enabledFeatures.includes('advisor_full_insurance') ||
              enabledFeatures.includes('omp_financial_data_sq')
            ) {
              this.peopleRelations = getRelations(
                response.data.testator.people
              );
              this.loading = false;
            } else {
              this.$router.push({ name: 'OverviewPage' });
            }
          }
        },
      },
    },
  };
</script>

<style lang="scss">
  .insurance-container {
    padding-top: 28px;

    .mobile-omart-image {
      display: none;
    }
    .desktop-omart-image {
      display: block;

      .v-image {
        height: 100%;
      }
    }

    .center-insurance-button {
      display: block;
      margin: auto;
      margin-top: 20px;
      margin-bottom: 20px;
    }

    .omart-image {
      margin: 10px auto 0 auto;
    }

    .insurance-img {
      margin: 12px auto 12px auto;
    }

    .insurance-img-xs {
      max-width: 50%;
      margin: 0 auto 20px auto;
    }
  }

  .omart-container {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 60px;
  }

  .support-snippet-container {
    margin-bottom: 64px;
  }

  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .insurance-container {
      .mobile-omart-image {
        display: flex;
        justify-content: center;
        width: 100%;
      }
      .desktop-omart-image {
        display: none;
      }
    }
  }
</style>
